import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";

/**
 * useContribuyentesSearch — hook reutilizable para search server-side de contribuyentes
 * con debounce y merge/dedup automático con la lista ya cargada.
 *
 * Consolida la lógica que estaba duplicada en LiquidacionesPage y RecaudacionesPage.
 *
 * @param {Object} opts
 * @param {number} [opts.initialLimit=200]  Cuántos cargar al montar (para poblar el Select inicial)
 * @param {number} [opts.searchLimit=100]   Cuántos traer por búsqueda
 * @param {number} [opts.minChars=2]        Mínimo de chars para disparar búsqueda server-side
 * @param {number} [opts.debounceMs=250]    Debounce del input
 * @param {string|null} [opts.clase]        Filtro extra (`"con-lae"` o `null` para todos)
 *
 * @returns {Object}
 * @returns {Array}    return.contribuyentes    Lista actual (merge de iniciales + búsquedas)
 * @returns {string}   return.contribSearch     Valor actual del input de búsqueda
 * @returns {Function} return.setContribSearch  Setter del input
 * @returns {Function} return.setContribuyentes Setter directo (por si necesitás resetear)
 * @returns {boolean}  return.loading           true mientras la búsqueda server-side está en curso
 */
export function useContribuyentesSearch({
  initialLimit = 200,
  searchLimit = 100,
  minChars = 2,
  debounceMs = 250,
  clase = null,
} = {}) {
  const [contribuyentes, setContribuyentes] = useState([]);
  const [contribSearch, setContribSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${sessionStorage.getItem("sisaur_token")}` } };

  // Carga inicial
  useEffect(() => {
    const params = { limit: initialLimit };
    if (clase) params.clase = clase;
    axios.get(`${API}/contribuyentes`, { ...auth, params })
      .then(r => setContribuyentes(r.data || []))
      .catch(() => setContribuyentes([]));
    // eslint-disable-next-line
  }, [initialLimit, clase]);

  // Búsqueda server-side con debounce + merge dedup por id
  useEffect(() => {
    const q = contribSearch.trim();
    if (q.length < minChars) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { search: q, limit: searchLimit };
        if (clase) params.clase = clase;
        const r = await axios.get(`${API}/contribuyentes`, { ...auth, params });
        const nuevos = r.data || [];
        setContribuyentes(prev => {
          const map = new Map(prev.map(c => [c.id, c]));
          nuevos.forEach(c => map.set(c.id, c));
          return Array.from(map.values());
        });
      } catch (_) { /* ignore */ } finally {
        setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [contribSearch, minChars, searchLimit, debounceMs, clase]);

  return { contribuyentes, setContribuyentes, contribSearch, setContribSearch, loading };
}
